import { CombinedState, createSelector } from '@reduxjs/toolkit'
import { useFlag, useLDClient } from '@afterpaytouch/feature'
import { useEffect, useState } from 'react'
import { Cohort } from '@afterpaytouch/portal-api'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { PaymentType } from '@afterpay/types'
import {
  CheckoutAppSelector,
  CheckoutAppState,
  CheckoutAppStore,
  ExperimentsState,
  triggerExperiment,
  useActiveCashAppPay,
  useAppDispatch,
  useAppSelector,
  useCohort,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useIsEmptyCheckoutToken,
  usePreferredCard,
  useShouldSetApplePayAsPurchaseMethod,
  useShouldUseBankAccount,
} from '../'
import { Experiment, Experiments, ExperimentValue, Features, getExperiment, KnownExperiment } from '../../model'
import { useStore } from 'react-redux'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'

export const experimentsSelector: CheckoutAppSelector<ExperimentsState> = (state) => state.experiments

export const activeExperiments: CheckoutAppSelector<string> = createSelector(experimentsSelector, ({ activeExperiments }) => {
  return Object.entries(activeExperiments)
    .map(([experiment, value]) => `${experiment}${value}`)
    .join(',')
})

const activeExperimentsSelector = (state: CombinedState<CheckoutAppState>): Partial<Record<KnownExperiment, ExperimentValue>> => {
  return state.experiments.activeExperiments
}

export const useExperimentsState = (): ExperimentsState => useAppSelector(experimentsSelector)

export const useActiveExperiments = (): string => useAppSelector(activeExperiments)

export const useTriggerExperimentEvent = (): ((params: Experiment, customEventProperties?: Record<string, unknown>) => void) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const isEmptyCheckoutToken: boolean = useIsEmptyCheckoutToken()
  return ({ name, value, control }: Experiment, customEventProperties?: Record<string, unknown>): void => {
    const activeExperiments = activeExperimentsSelector(store.getState())
    const analyticsFired = name in activeExperiments
    if (!analyticsFired && !isEmptyCheckoutToken) {
      dispatch(triggerExperiment([name, value]))
      logEvent('Triggered Experiment', {
        experimentName: Experiments[name],
        experimentValue: value,
        experimentControl: control,
        experimentFlag: `${name}${value}`,
        ...customEventProperties,
      })
    }
  }
}

export const useExperimentTrigger = (experiment: Experiment): ((customEventProperties?: Record<string, unknown>) => void) => {
  const [fire, setFired] = useState(false)
  const [customEventProperties, setCustomEventProperties] = useState({})
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (experiment.active && !experiment.loading && fire) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }, [experiment.name, experiment.active, experiment.value, fire, triggerExperimentEvent, experiment, customEventProperties])

  return (customEventProperties) => {
    setFired(true)
    if (customEventProperties !== undefined) {
      setCustomEventProperties(customEventProperties)
    }
  }
}

// delays experiment trigger until experiment is passed in
export const useDelayedExperimentTrigger = (): ((updatedExperiment: Experiment) => void) => {
  const [{ isFired, givenExperiment }, setFiredExperiment] = useState<{ isFired: boolean; givenExperiment: Experiment | undefined }>({
    isFired: false,
    givenExperiment: undefined,
  })
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (givenExperiment === undefined) {
      return
    }

    if (givenExperiment.active && !givenExperiment.loading && isFired) {
      triggerExperimentEvent(givenExperiment)
    }
  }, [givenExperiment, givenExperiment?.name, givenExperiment?.active, givenExperiment?.value, givenExperiment?.control, isFired, triggerExperimentEvent])

  return (experiment) =>
    setFiredExperiment({
      isFired: true,
      givenExperiment: experiment,
    })
}

export function useExperiment(name: KnownExperiment, fireEvent: boolean = true, customisedExperiment: Experiment | null = null): Experiment {
  const client = useLDClient()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { activeExperiments } = useExperimentsState()
  let experiment = customisedExperiment
  if (experiment === null) {
    const ldValue = client?.variationDetail(Experiments[name], false)
    experiment = getExperiment(name, ldValue, activeExperiments[name])
  }

  const experimentTrigger = useExperimentTrigger(experiment)

  useEffect(() => {
    if (fireEvent && consumerFeatureFlagReady) {
      experimentTrigger()
    }
  }, [consumerFeatureFlagReady, experimentTrigger, fireEvent])

  return experiment
}

export const useIsEligibleForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  // Mirrors the routing in LoggedIn.tsx — only users who would actually be sent down the
  // experimental no-payment-method branch should be enrolled, otherwise variant A and control
  // behave identically and dilute the analysis population. `selectPreferredCard` collapses the
  // full saved-card / fallback state into preferredCard.card: it's null only when the user has
  // no preferred and no usable fallback, and non-null in every other case (including the
  // errored-preferred-with-no-fallback edge), so this check matches LoggedIn's
  // `preferredCard?.card !== null` routing guard exactly.
  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    addPaymentMethodFlowExperimentValue === 'A',
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    !isPaymentMethodEligible,
    hasNoUsablePaymentMethod,
  ]
  return eligibilityConditions.every((condition) => condition)
}

// Symmetric to useIsEligibleForAddPaymentMethodFlowExperiment but for the control arm. Returns
// true iff the user is a variant-C participant who would be routed to the standard
// add-payment-method page in LoggedIn.tsx. Same cohort/design/lending/no-payment-method gates
// as Test — the only difference is the variant check. Also requires `experiment.active` and
// !experiment.loading because `getExperiment` defaults `value` to 'C' while LD is loading
// (`apps/checkout/src/model/experiments.tsx`'s `loading` constant), and we must not enroll
// users whose variant assignment hasn't actually resolved yet.
//
// Background: Amplitude variant-C `Triggered Experiment` events dropped from ~30-60/day to 0
// on 2026-05-06 (the day PR #11859 deployed). The original eligibility hook only ever fires
// the trigger for variant=A users, so it can't have been the source of the pre-2026-05-06
// variant-C events — that means some other implicit mechanism was firing them and stopped.
// Restoring symmetric explicit firing here closes the gap without depending on whatever
// implicit mechanism existed before; the dispatcher's analyticsFired dedup in
// useTriggerExperimentEvent bounds any future double-firing to one event per session.
export const useIsControlForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value, active, loading } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  return active && !loading && value === 'C' && cohort !== Cohort.CashOnly && !isBronteDesignLanguage && !isPaymentMethodEligible && hasNoUsablePaymentMethod
}

const usePi4SwitcherNoPaymentMethodExperimentEligibility = (
  selectedPaymentType?: PaymentType
): { eligible: boolean; experimentValue: ExperimentValue; experimentActive: boolean; experimentLoading: boolean } => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue, active, loading } = experiment
  const isPi4SwitcherSummaryFirstEnabled = useFlag(Features.CHECKOUT_ENABLE_ADD_PAYMENT_METHOD_PM_PI4_SWITCHER_SUMMARY_FIRST, false)
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    isPi4SwitcherSummaryFirstEnabled,
    selectedPaymentType === PaymentType.PBI,
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    isConsumerLendingEligible,
    hasNoUsablePaymentMethod,
  ]

  return {
    eligible: eligibilityConditions.every((condition) => condition),
    experimentValue: addPaymentMethodFlowExperimentValue,
    experimentActive: active,
    experimentLoading: loading,
  }
}

export const useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && (experimentValue === 'A' || experimentValue === 'C')
}

export const useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && experimentValue === 'A'
}

// Returns a callback that fires the Triggered Experiment event for the current add-payment-method
// experiment assignment. Caller is responsible for invoking this only when the user is actually
// routed through a no-payment-method branch (currently from LoggedIn.tsx, gated on requestCompleted +
// hasFeatureFlagsReady). The eligibility hooks above intentionally do NOT auto-fire because
// `preferredCard?.card === null` can be transiently true (e.g. after `resetPreferredPaymentMethodErrors`
// mutates the card field, or before `setCardsForExpiryLimit` runs with a populated `started` payload),
// which previously enrolled users with saved cards and diluted the variant-A pool.
//
// Dispatches synchronously rather than using the `useExperimentTrigger` setState+useEffect
// indirection: callers invoke this immediately before `router.replace`, so the LoggedIn page
// can unmount in the next render and skip a deferred effect. Calling `useTriggerExperimentEvent`
// directly guarantees the Redux dispatch + analytics logEvent happen in the same execution as
// the trigger() invocation.
export const useAddPaymentMethodFlowExperimentTrigger = (): ((customEventProperties?: Record<string, unknown>) => void) => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const triggerExperimentEvent = useTriggerExperimentEvent()
  return (customEventProperties) => {
    if (experiment.active && !experiment.loading) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }
}
